import { join } from 'juriquery'

import { PaginationListingParams, Query } from '../types'

const getRouteWithInjectedId = (route: string, id: number) =>
  route.replace(':id', `${id}`)

export const SETTINGS_ROOT_ROUTE = '/settings'
export const getSettingsRoute = () => `${SETTINGS_ROOT_ROUTE}`

// security
export const SETTINGS_SECURITY_ROUTE = 'security'

export const getSettingsSecurityRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_SECURITY_ROUTE}`

// clinic
export const SETTINGS_CLINIC_ROUTE = 'clinic'
export const SETTINGS_CLINIC_GENERAL_ROUTE = 'general'
export const SETTINGS_CLINIC_WORKING_SCHEDULE_ROUTE = 'working-schedule'

export const getSettingsClinicRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_CLINIC_ROUTE}`

export const getSettingsClinicGeneralRoute = () =>
  `${getSettingsClinicRoute()}/${SETTINGS_CLINIC_GENERAL_ROUTE}`

export const getSettingsClinicWorkingScheduleRoute = () =>
  `${getSettingsClinicRoute()}/${SETTINGS_CLINIC_WORKING_SCHEDULE_ROUTE}`

// devices
export const SETTINGS_DEVICES_ROUTE = 'devices'
export const SETTINGS_DEVICES_ADD_ROUTE = 'add'
export const SETTINGS_DEVICE_EDIT_ROUTE = ':id/edit'
export const SETTINGS_DEVICE_AUTHORIZATION_ROUTE = ':id/authorization'

const getRouteWithInjectedDeviceId = (route: string, deviceId: number) =>
  route.replace(':id', `${deviceId}`)

export const getSettingsDevicesRoute = (
  page?: number,
  limit?: number,
  search?: string,
) =>
  join(`${getSettingsRoute()}/${SETTINGS_DEVICES_ROUTE}`, {
    page,
    limit,
    search,
  })

export const getSettingsDeviceEditRoute = (deviceId: number) =>
  `${getSettingsDevicesRoute()}/${getRouteWithInjectedDeviceId(
    SETTINGS_DEVICE_EDIT_ROUTE,
    deviceId,
  )}`

export const getSettingsDeviceAuthorizationRoute = (deviceId: number) =>
  `${getSettingsDevicesRoute()}/${getRouteWithInjectedDeviceId(
    SETTINGS_DEVICE_AUTHORIZATION_ROUTE,
    deviceId,
  )}`

export const getSettingsDevicesAddRoute = () =>
  `${getSettingsDevicesRoute()}/${SETTINGS_DEVICES_ADD_ROUTE}`

// credentialing
export const SETTINGS_CREDENTIALING_ROUTE = 'credentialing'
export const SETTINGS_CREDENTIALING_REMINDERS_ROUTE = 'reminders'
export const SETTINGS_CREDENTIALING_ROLES_ROUTE = 'roles'
export const SETTINGS_CREDENTIALING_ROLES_CREATE_ROUTE = 'roles/create'
export const SETTINGS_CREDENTIALING_ROLE_TEMPLATES_ROUTE = 'roles/:id/templates'
export const SETTINGS_CREDENTIALING_ROLE_TEMPLATES_EDIT_ROUTE =
  'roles/:id/templates/edit'
export const SETTINGS_CREDENTIALING_ROLE_FOLDERS_ROUTE = 'roles/:id/folders'

export const getSettingsCredentialingRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_CREDENTIALING_ROUTE}`

export const getSettingsCredentialingRemindersRoute = () =>
  `${getSettingsCredentialingRoute()}/${SETTINGS_CREDENTIALING_REMINDERS_ROUTE}`

export const getSettingsCredentialingRolesRoute = (
  page?: number,
  limit?: number,
  search?: string,
) =>
  join(
    `${getSettingsCredentialingRoute()}/${SETTINGS_CREDENTIALING_ROLES_ROUTE}`,
    {
      page,
      limit,
      search,
    },
  )

export const getSettingsCredentialingRolesCreateRoute = () =>
  `${getSettingsCredentialingRoute()}/${SETTINGS_CREDENTIALING_ROLES_CREATE_ROUTE}`

export const getSettingsCredentialingRoleTemplatesRoute = (roleId: number) =>
  `${getSettingsCredentialingRoute()}/${getRouteWithInjectedId(
    SETTINGS_CREDENTIALING_ROLE_TEMPLATES_ROUTE,
    roleId,
  )}`

export const getSettingsCredentialingRoleTemplatesEditRoute = (
  roleId: number,
) =>
  `${getSettingsCredentialingRoute()}/${getRouteWithInjectedId(
    SETTINGS_CREDENTIALING_ROLE_TEMPLATES_EDIT_ROUTE,
    roleId,
  )}`

export const getSettingsCredentialingRoleFoldersRoute = (roleId: number) =>
  `${getSettingsCredentialingRoute()}/${getRouteWithInjectedId(
    SETTINGS_CREDENTIALING_ROLE_FOLDERS_ROUTE,
    roleId,
  )}`

// roles
export const SETTINGS_ROLES_ROUTE = 'roles'
export const SETTINGS_ROLES_SYSTEM_ROUTE = 'system'
export const SETTINGS_ROLES_SYSTEM_CREATE_ROUTE = 'system/create'
export const SETTINGS_ROLES_SYSTEM_ROLE_EDIT_ROUTE = 'system/:id/edit'
export const SETTINGS_ROLES_CONTENT_ROUTE = 'content'
export const SETTINGS_ROLES_CONTENT_CREATE_ROUTE = 'content/create'
export const SETTINGS_ROLES_CONTENT_ROLE_EDIT_ROUTE = 'content/:id/edit'

export const getSettingsRolesRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_ROLES_ROUTE}`

export const getSettingsRolesSystemRoute = ({
  page,
  limit,
  search,
}: PaginationListingParams = {}) =>
  join(`${getSettingsRolesRoute()}/${SETTINGS_ROLES_SYSTEM_ROUTE}`, {
    page,
    limit,
    search,
  })

export const getSettingsRolesContentRoute = ({
  page,
  limit,
  search,
}: PaginationListingParams = {}) =>
  join(`${getSettingsRolesRoute()}/${SETTINGS_ROLES_CONTENT_ROUTE}`, {
    page,
    limit,
    search,
  })

export const getSettingsRolesSystemCreateRoute = () =>
  `${getSettingsRolesRoute()}/${SETTINGS_ROLES_SYSTEM_CREATE_ROUTE}`

export const getSettingsRolesContentCreateRoute = () =>
  `${getSettingsRolesRoute()}/${SETTINGS_ROLES_CONTENT_CREATE_ROUTE}`

export const getSettingsRolesContentRoleEditRoute = (roleId: number) =>
  `${getSettingsRolesRoute()}/${getRouteWithInjectedId(
    SETTINGS_ROLES_CONTENT_ROLE_EDIT_ROUTE,
    roleId,
  )}`

export const getSettingsRolesSystemRoleEditRoute = (roleId: number) =>
  `${getSettingsRolesRoute()}/${getRouteWithInjectedId(
    SETTINGS_ROLES_SYSTEM_ROLE_EDIT_ROUTE,
    roleId,
  )}`

// Access management
export const SETTINGS_ACCESS_MANAGEMENT_ROUTE = 'access-management'
export const SETTINGS_ACCESS_MANAGEMENT_GROUPS_ROUTE = 'groups'
export const SETTINGS_ACCESS_MANAGEMENT_GROUPS_CREATE_ROUTE = 'groups/create'
export const SETTINGS_ACCESS_MANAGEMENT_GROUP_ROUTE = 'groups/:id'
export const SETTINGS_ACCESS_MANAGEMENT_GROUP_EDIT_ROUTE = 'groups/:id/edit'
export const SETTINGS_ACCESS_MANAGEMENT_USERS_ROUTE = 'users'
export const SETTINGS_ACCESS_MANAGEMENT_USER_ROUTE = 'users/:id'

export const getSettingsAccessManagementRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_ACCESS_MANAGEMENT_ROUTE}`

export const getSettingsAccessManagementGroupsRoute = () =>
  `${getSettingsAccessManagementRoute()}/${SETTINGS_ACCESS_MANAGEMENT_GROUPS_ROUTE}`

export const getSettingsAccessManagementGroupRoute = (groupId: number) =>
  `${getSettingsAccessManagementRoute()}/${getRouteWithInjectedId(
    SETTINGS_ACCESS_MANAGEMENT_GROUP_ROUTE,
    groupId,
  )}`

export const getSettingsAccessManagementGroupEditRoute = (groupId: number) =>
  `${getSettingsAccessManagementRoute()}/${getRouteWithInjectedId(
    SETTINGS_ACCESS_MANAGEMENT_GROUP_EDIT_ROUTE,
    groupId,
  )}`

export const getSettingsAccessManagementGroupsCreateRoute = () =>
  `${getSettingsAccessManagementRoute()}/${SETTINGS_ACCESS_MANAGEMENT_GROUPS_CREATE_ROUTE}`

export const getSettingsAccessManagementUsersRoute = () =>
  `${getSettingsAccessManagementRoute()}/${SETTINGS_ACCESS_MANAGEMENT_USERS_ROUTE}`

export const getSettingsAccessManagementUserRoute = (id: number) =>
  `${getSettingsAccessManagementRoute()}/${getRouteWithInjectedId(
    SETTINGS_ACCESS_MANAGEMENT_USER_ROUTE,
    id,
  )}`

// users
export const SETTINGS_USERS_ROUTE = 'users'
export const SETTINGS_USERS_CREATE_ROUTE = 'create'
export const SETTINGS_USER_EDIT_ROUTE = ':id/edit'
export const SETTINGS_USER_CONVERT_ROUTE = ':id/convert'

export const getSettingsUsersRoute = (
  query?: { page?: number; limit?: number; search?: string } & Query,
) => join(`${getSettingsRoute()}/${SETTINGS_USERS_ROUTE}`, query)

export const getSettingsUsersCreateUserRoute = () =>
  `${getSettingsUsersRoute()}/${SETTINGS_USERS_CREATE_ROUTE}`

export const getSettingsUserEditRoute = (userId: number) =>
  `${getSettingsUsersRoute()}/${getRouteWithInjectedId(
    SETTINGS_USER_EDIT_ROUTE,
    userId,
  )}`

export const getSettingsUserConvertUserRoute = (userId: number) =>
  `${getSettingsUsersRoute()}/${getRouteWithInjectedId(
    SETTINGS_USER_CONVERT_ROUTE,
    userId,
  )}`

// notifications
export const SETTINGS_NOTIFICATIONS_ROUTE = 'notifications'
export const SETTINGS_NOTIFICATIONS_MODULE_ROUTE = 'modules/:module'

const getRouteWithInjectedModule = (route: string, module: string) =>
  route.replace(':module', `${module}`)

export const getSettingsNotificationsRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_NOTIFICATIONS_ROUTE}`

export const getSettingsNotificationsModuleRoute = (module: string) =>
  `${getSettingsNotificationsRoute()}/${getRouteWithInjectedModule(
    SETTINGS_NOTIFICATIONS_MODULE_ROUTE,
    module,
  )}`

// logging
export const SETTINGS_LOGGING_ROUTE = 'logging'
export const SETTINGS_LOGGING_GENERAL_ROUTE = 'general'

export const getSettingsLoggingRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_LOGGING_ROUTE}`

export const getSettingsLoggingGeneralRoute = () =>
  `${getSettingsLoggingRoute()}/${SETTINGS_LOGGING_GENERAL_ROUTE}`

// list manager
export const SETTINGS_LIST_MANAGER_ROUTE = 'list-manager'
export const SETTINGS_LIST_MANAGER_ADD_ROUTE = 'add'
export const SETTINGS_LIST_MANAGER_EDIT_LIST_ROUTE = 'edit/:id'

export const getSettingsListManagerRoute = () =>
  `${getSettingsRoute()}/${SETTINGS_LIST_MANAGER_ROUTE}`

export const getSettingsListManagerAddRoute = () =>
  `${getSettingsListManagerRoute()}/${SETTINGS_LIST_MANAGER_ADD_ROUTE}`

export const getSettingsListManagerEditListRoute = (id: number) =>
  `${getSettingsListManagerRoute()}/${getRouteWithInjectedId(
    SETTINGS_LIST_MANAGER_EDIT_LIST_ROUTE,
    id,
  )}`
